import { createTheme, ThemeProvider } from '@mui/material/styles';
import * as React from 'react';
import { defaultPaletteMode, getArideCustomColors, getAridePalette } from './colors';
import { buildTypographyOptions, fontFamilyPrimary } from './tokens/typography';
import { borderWidth, radius } from './tokens/shape';
import { spacingFromFactor } from './tokens/spacing';
import { elevationOffsetPx } from './tokens/elevation';
import type { ArideCustomColors } from './tokens/buildCustomColors';
import type { AridePaletteMode } from './tokens/buildPalette';

declare module '@mui/material/styles' {
  interface Theme {
    customColors: ArideCustomColors;
  }
  interface ThemeOptions {
    customColors?: ArideCustomColors;
  }
}

export interface ThemeProps {
  children?: React.ReactNode;
}

type ThemeModeContextValue = {
  mode: AridePaletteMode;
  toggleMode: () => void;
};

// Default seguro: consumidores de `useThemeMode` (testes unitários, render isolado) podem montar fora
// do `<Theme>` sem crashar. Em produção o App sempre envolve com `<Theme>`, então o valor real prevalece.
const defaultThemeModeValue: ThemeModeContextValue = {
  mode: defaultPaletteMode,
  toggleMode: () => {}
};

const ThemeModeContext = React.createContext<ThemeModeContextValue>(defaultThemeModeValue);

export function buildArideTheme(mode: AridePaletteMode) {
  const palette = getAridePalette(mode);
  const customColors = getArideCustomColors(mode);
  const baseText = palette.text?.primary ?? '#121212';
  const secondaryText = palette.text?.secondary ?? '#8c8f97';

  return createTheme({
    spacing: spacingFromFactor,
    palette,
    customColors,
    shape: {
      borderRadius: radius.small
    },
    typography: buildTypographyOptions(baseText, secondaryText),
    shadows: [
      'none',
      `0px ${elevationOffsetPx}px ${elevationOffsetPx}px rgba(0,0,0,0.2)`,
      `0px ${elevationOffsetPx}px 8px rgba(0,0,0,0.22)`,
      `0px ${elevationOffsetPx}px 12px rgba(0,0,0,0.24)`,
      `0px ${elevationOffsetPx}px 16px rgba(0,0,0,0.26)`,
      `0px ${elevationOffsetPx}px 20px rgba(0,0,0,0.28)`,
      `0px ${elevationOffsetPx}px 24px rgba(0,0,0,0.3)`,
      `0px ${elevationOffsetPx}px 28px rgba(0,0,0,0.32)`,
      `0px ${elevationOffsetPx}px 32px rgba(0,0,0,0.34)`,
      `0px ${elevationOffsetPx}px 36px rgba(0,0,0,0.36)`,
      `0px ${elevationOffsetPx}px 40px rgba(0,0,0,0.38)`,
      `0px ${elevationOffsetPx}px 44px rgba(0,0,0,0.4)`,
      `0px ${elevationOffsetPx}px 48px rgba(0,0,0,0.42)`,
      `0px ${elevationOffsetPx}px 52px rgba(0,0,0,0.44)`,
      `0px ${elevationOffsetPx}px 56px rgba(0,0,0,0.46)`,
      `0px ${elevationOffsetPx}px 60px rgba(0,0,0,0.48)`,
      `0px ${elevationOffsetPx}px 64px rgba(0,0,0,0.5)`,
      `0px ${elevationOffsetPx}px 68px rgba(0,0,0,0.52)`,
      `0px ${elevationOffsetPx}px 72px rgba(0,0,0,0.54)`,
      `0px ${elevationOffsetPx}px 76px rgba(0,0,0,0.56)`,
      `0px ${elevationOffsetPx}px 80px rgba(0,0,0,0.58)`,
      `0px ${elevationOffsetPx}px 84px rgba(0,0,0,0.6)`,
      `0px ${elevationOffsetPx}px 88px rgba(0,0,0,0.62)`,
      `0px ${elevationOffsetPx}px 92px rgba(0,0,0,0.64)`,
      `0px ${elevationOffsetPx}px 96px rgba(0,0,0,0.66)`
    ],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily: fontFamilyPrimary,
            backgroundColor: palette.background?.default
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: radius.large
          },
          contained: ({ theme }) => ({
            fontSize: theme.typography.button.fontSize
          }),
          outlined: ({ theme }) => ({
            fontSize: theme.typography.button.fontSize
          }),
          text: ({ theme }) => ({
            color: theme.palette.text.primary,
            fontSize: theme.typography.button.fontSize
          }),
          textPrimary: ({ theme }) => ({
            color: theme.palette.text.primary,
            fontSize: theme.typography.button.fontSize
          }),
          textSecondary: ({ theme }) => ({
            color: theme.palette.text.secondary,
            fontSize: theme.typography.button.fontSize
          }),
          textInherit: ({ theme }) => ({
            color: theme.palette.text.primary,
            fontSize: theme.typography.button.fontSize
          })
        }
      },
      MuiModal: {
        styleOverrides: {
          root: {
            padding: 3
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          // Borda do cartão é mode-aware: no escuro o contorno sutil `gray[700]` (#3B3D42) sobre fundo
          // escuro define a borda (validado no Figma); no claro esse mesmo cinza vira uma borda escura
          // indesejada, então usamos `grey[300]` (#DDDEE1) — análogo claro e sutil ao do tema escuro.
          root: ({ theme }) => ({
            backgroundColor: theme.palette.background.paper,
            backgroundImage: 'none',
            borderRadius: radius.medium,
            borderColor: theme.palette.mode === 'dark' ? theme.customColors.gray[700] : theme.palette.grey[300],
            borderWidth: borderWidth.small,
            borderStyle: 'solid'
          })
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: ({ theme }) => ({
            backgroundColor: theme.palette.grey[50],
            borderRadius: radius.small,
            // Sem sombra o balão fundia-se com os cartões claros por baixo.
            boxShadow: '0px 4px 16px rgba(0,0,0,0.24)',
            color: theme.palette.grey[900],
            fontSize: '0.75rem',
            lineHeight: 1.5,
            maxWidth: 320,
            padding: '12px'
          }),
          arrow: ({ theme }) => ({ color: theme.palette.grey[50] })
        }
      }
    }
  });
}

const Theme: React.FC<ThemeProps> = ({ children }) => {
  const [mode, setMode] = React.useState<AridePaletteMode>(defaultPaletteMode);
  const theme = React.useMemo(() => buildArideTheme(mode), [mode]);
  const toggleMode = React.useCallback(() => {
    setMode((current) => (current === 'dark' ? 'light' : 'dark'));
  }, []);
  const value = React.useMemo(() => ({ mode, toggleMode }), [mode, toggleMode]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export function useThemeMode() {
  return React.useContext(ThemeModeContext);
}

export default Theme;
